import gsap, { Power3 } from 'gsap';
import { GetBy } from '../_app/cuchillo/core/Element';
import EventDispatcher from '../_app/cuchillo/core/EventDispatcher';

export default class VideoMultiple {
  container;
  containerVideos;
  shadow;
  videos = [];

  current = 0;
  total = 0;
  totalTime = 0;
  isPlaying = false;

  screenTime = "00:00:00";
  onChange = null;

  _currentTime = 0;
  segmentDuration = 5; // configurable

  constructor(dom) {
    this.container = dom;
    this.shadow = GetBy.class("__shadow", this.container)[0];
    this.containerVideos = GetBy.class("__holder", this.container)[0];
    this.videos = [...GetBy.selector("[data-multivideo]")].map(v => new VideoMultiple__Video(v));

    this.total = this.videos.length;
    this.totalTime = this.total * this.segmentDuration;

    // Eventos globales
    EventDispatcher.addEventListener("FOCUS_ON", () => this.goFocus(), "VIDEO_01");
    EventDispatcher.addEventListener("FOCUS_OFF", () => this.goNormal(), "VIDEO_02");
  }

  /** Getter/Setter de tiempo */
  get currentTime() { return this._currentTime; }
  set currentTime(time) {
    this._currentTime = time;
    this.videos[this.current].currentTime = this._currentTime - this.segmentDuration * this.current;
  }

  /** Getter/Setter de progreso (0–1) */
  get progress() { return this._currentTime / this.totalTime; }
  set progress(n) { this._currentTime = this.totalTime * n; }

  /** Getter/Setter de progreso (0–1) */
  get isReady() { 
    return this.videos[this.current].isReady
  }
  

  /** Animaciones de enfoque */
  /*goFocus() {
    gsap.to(this.shadow, { alpha: 1, ease: Power3.easeOut, duration: 1 });
    gsap.to(this.containerVideos, { scale: 1.4, ease: Power3.easeOut, duration: 1 });
  }

  goNormal() {
    gsap.to(this.shadow, { alpha: 0, ease: Power3.easeOut, duration: 0.8 });
    gsap.to(this.containerVideos, { scale: 1, ease: Power3.easeOut, duration: 0.6 });
  }*/

  goFocus() {
    gsap.killTweensOf([this.shadow, this.container]);
    gsap.to(this.shadow, { alpha: 1, delay:0, ease: Power3.easeOut, duration: .6 });
    gsap.to(this.containerVideos, { scale: 1.1, ease: Power3.easeOut, duration: .6 });
  }

  goNormal() {
    gsap.killTweensOf([this.shadow, this.container]);
    gsap.to(this.shadow, { alpha: 0, ease: Power3.easeOut, duration: 0.2 });
    gsap.to(this.containerVideos, { scale: 1, ease: Power3.easeOut, duration: 0.6 });
  }

  /** Cambiar el tiempo global */
  changeTime(time) {
    let newTime = time;

    // Loop infinito
    if (newTime > this.totalTime) newTime -= this.totalTime;
    if (newTime < 0) newTime += this.totalTime;

    this._switchSegment(newTime);
    this.currentTime = newTime;
    this.loop();
  }

  /** Verificar si hay que cambiar de segmento según el tiempo */
  checkCurrentTime() {
    this._switchSegment(this.currentTime);
  }

  /** Interno → cambiar de vídeo */
  _switchSegment(time) {
    let index = Math.floor(time / this.segmentDuration);
    if (index >= this.total) index = 0;

    if (index !== this.current) {
      this.videos[this.current].hide();
      this.current = index;
      this.videos[this.current].show();
      this._preloadNeighbors();

      this.onChange?.(this.current);
    }
  }

  /** Precarga el anterior y siguiente video */
  _preloadNeighbors() {
    const prev = (this.current - 1 + this.total) % this.total;
    const next = (this.current + 1) % this.total;

    this.videos[prev].preload();
    this.videos[next].preload();
  }

  show() {
    this.play();
    gsap.to(this.containerVideos, { opacity:1, scale: 1, delay:.1, ease: Power3.easeOut, duration: 0.6 });
  } 


  play() {
    this.isPlaying = true;
    this.videos[this.current].show();
    this._preloadNeighbors(); // precarga al arrancar
    this.onChange?.(this.current);
  }

  pause() {
    this.isPlaying = false;
  }

  loop() {
    if (!this.isPlaying) return;

    this.checkCurrentTime();
    this._currentTime = this.videos[this.current].currentTime + this.segmentDuration * this.current;
    this.screenTime = this._formatTime(this._currentTime);
  }

  /** Formato tiempo → mm:ss:cc */
  _formatTime(sec) {
    const mins = Math.floor(sec / 60);
    const secs = Math.floor(sec % 60);
    const cents = Math.floor((sec % 1) * 100);

    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}:${cents.toString().padStart(2, "0")}`;
  }

  /** Limpieza */
  dispose() {
    this.pause();

    // Eliminar listeners globales
    EventDispatcher.removeEventListener("FOCUS_ON", this.goFocus, "VIDEO_01");
    EventDispatcher.removeEventListener("FOCUS_OFF", this.goNormal, "VIDEO_02");

    // Limpiar videos
    this.videos.forEach(v => v.dispose());
    this.videos = [];

    // Resetear estado
    this.isPlaying = false;
    this.current = 0;
    this._currentTime = 0;

    // Soltar referencias DOM
    this.container = null;
    this.shadow = null;
    this.containerVideos = null;
    this.onChange = null;
  }
}

class VideoMultiple__Video {
  _video;
  isReady = false;
  src;

  constructor(videoEl) {
    this._video = videoEl;
    this._video.muted = true;   // evita bloqueos de autoplay
    this._video.loop = true;
    this.src = this._video.getAttribute("src");

    this.hide();
    this._setupEvents();
  }

  get currentTime() { return this._video.currentTime; }
  set currentTime(n) { this._video.currentTime = n; }

  _setupEvents() {
    this._calls = {
      canplay: () => { 
        this.isReady = true;
      }
    };
    this._video.addEventListener("canplaythrough", this._calls.canplay);
  }

  play() {
    this._video.setAttribute("src", this.src);
    this._video.play().catch(err => {
      console.warn("No se pudo reproducir el video automáticamente:", err);
    });
  }

  show() {
    this._video.parentNode.style.opacity = 1;
    this._video.parentNode.style.pointerEvents = "auto";
    this._video.currentTime = 0;
    this.play();
  }

  hide() {
    this.stop();
    this._video.parentNode.style.opacity = 0;
    this._video.parentNode.style.pointerEvents = "none";
  }

  preload() {
    if (!this.isReady && this._video) {
      this._video.load();
    }
  }

  stop() {
    console.log("STOP")
    this._video.pause();
    this._video.setAttribute("src", "");
  }

  dispose() {
    this.stop();

    // Eliminar listener
    if (this._calls?.canplay) {
      this._video.removeEventListener("canplaythrough", this._calls.canplay);
    }

    // Liberar vídeo del navegador
    this._video.removeAttribute("src");
    this._video.load();

    // Resetear flags
    this.isReady = false;

    // Soltar referencias
    this._video = null;
    this.src = null;
    this._calls = null;
  }
}
